import { CurrencyPipe } from "@angular/common";

// Response from CBOSS CPP System
export class AuthorizationResponse {
    Number: string;
    Amount: string;
    OriginatorID: string;
    Token: string;
    Status: string;
    Error: string;
    ErrorMessage: string;
    SystemError: string;
    ItemCount: string;
    BillingAddressLine1: string;
    BillingCity: string;
    BillingFirstName: string;
    BillingLastName: string;
    BillingState: string;
    BillingEmailReceipt: string; //true/false
    BillingZip: string;
    BillingPhoneNumber: string;
    CreditCardNumber: string;
    CreditCardType: string;
    PaymentType: string;
    BillingEmail: string;
    DateAuthorized: string;
}


export class PaymentItem {
    id: string;
    description: string;
    legalReference: string;
    fee: number;
    expediteFee: number;
    department: string;
    fund: string;
    ali: string;
    program: string;
    revenueSourceCode: string;
    reporting: string;
    accountDescription: string;
    workflowId: string;

    constructor(
        id: string,
        description: string,
        legalReference: string,
        fee: number,
        //expediteFee: number,
        department: string,
        fund: string,
        ali: string,
        program: string,
        revenueSourceCode: string,
        reporting: string,
        accountDescription: string,
        workflowId: string
    ) {
        this.id = id;
        this.description = description;
        this.legalReference = legalReference;
        this.fee = fee;
        //this.expediteFee = expediteFee;
        this.department = department;
        this.fund = fund;
        this.ali = ali;
        this.program = program;
        this.revenueSourceCode = revenueSourceCode;
        this.reporting = reporting;
        this.accountDescription = accountDescription;
        this.workflowId = workflowId;
    }
}

export class LegacyPermitPayment
{
    feeAmount: string;
    checkNumber: string;
    revisedCheckNumber: string;
    revisedFeeAmount: string;
}

export class ApplicationCreditAuthorization extends AuthorizationResponse {
    id?: number;
    paymentRecords?: Payment[];

    parseResponse(response: AuthorizationResponse) {
        if (response != null) {
            this.Number = response.Number;
            this.Amount = response.Amount;
            this.OriginatorID = response.OriginatorID;
            this.Token = response.Token;
            this.Status = response.Status;
            this.Error = response.Error;
            this.ErrorMessage = response.ErrorMessage;
            this.SystemError = response.SystemError;
            this.ItemCount = response.ItemCount;
            this.BillingAddressLine1 = response.BillingAddressLine1;
            this.BillingCity = response.BillingCity;
            this.BillingFirstName = response.BillingFirstName;
            this.BillingLastName = response.BillingLastName;
            this.BillingState = response.BillingState;
            this.BillingEmailReceipt = response.BillingEmailReceipt;
            this.BillingZip = response.BillingZip;
            this.BillingPhoneNumber = response.BillingPhoneNumber;
            this.BillingEmail = response.BillingEmail;
            this.DateAuthorized = (response.DateAuthorized != null) ? response.DateAuthorized.replace('+', '') : "";
            //deliberately leaving out Credit Card Information so we don't store it
        }
    }
}



export interface Payment {
    id?: number;
    applicationPaymentId: string;
    amount: number;
    instrumentAmount: number;
    paymentType: string;
    paymentDate?: Date;
    token: string;
    tokenId?: string;
    paymentReceivedDate?: Date;
    paymentReceivedById?: string;
    paymentReceivedByName?: string;
    authorizationId?: number;
    authorization?: ApplicationCreditAuthorization
}

export interface ApplicationPayment {
    id: string;
    accountDescription?: string;
    ali?: string;
    amountDue: number;
    amountPaid: number;
    applicationDate: Date;
    applicationNumber: string;
    applicationType: string;
    department?: string;
    description?: string;
    fee: number;
    feeType?: string;
    fund?: string;
    invoice?: number;
    invoiceDocumentType?: string;
    isDeleted?: boolean;
    orderNumber?: string;
    organization?: string;
    organizationId: string;
    paymentDate?: Date;
    paymentStatus: PaymentStatus;
    program?: string;
    receipt?: number;
    receiptDocumentType?: string;
    reporting?: string;
    revenueSourceCode?: string;
    totalPayments: number;
    workflowId: string;
    queuedInvoice?: string;
    queuedReceipt?: string;
    submissionDate: Date;
    paymentReceivedDate?: Date;
    paymentReceivedById?: string;
    paymentReceivedByName?: string;
    paymentRecords: Payment[];
    workflowFieldId?: number;
    paymentPostedDate?: Date;
}

export class PaymentRowDisplay {

    get applicationNumber(): string {
        return this.applicationPayment.applicationNumber;
    }

    get applicationType(): string {
        return this.applicationPayment.applicationType;
    }

    get amountPaid(): number {
        return this.applicationPayment.amountPaid;
    }

    get amountDue(): number {
        return this.applicationPayment.amountDue;
    }

    get feeAmount(): number {
        return this.applicationPayment.fee;
    }

    get paymentToken(): string {
        if (this.paymentRecord == null) return "";
        return this.paymentRecord.token;
    }

    get applicationPaymentId(): string {
        return this.applicationPayment.id;
    }

    get recordId(): number {
        if (this.paymentRecord == null) return 0;
        return this.paymentRecord.id;
    }

    get paymentAmount(): number {
        if (this.paymentRecord == null) return 0;
        return this.paymentRecord.amount;
    }

    get paymentStatus(): PaymentStatus {
        return this.applicationPayment.paymentStatus;
    }

    get feeType(): string {
        return this.applicationPayment.feeType;
    }

    get feeDescription(): string {
        let feeWord = this.feeType.toLowerCase().trim().endsWith('fee') ? "" : "Fee ";
        return `${this.feeType} ${feeWord}in the amount of $${this.feeAmount}`;
    }


    private applicationPayment: ApplicationPayment;
    private paymentRecord: Payment;

    constructor(payment: ApplicationPayment, record: Payment) {
        this.applicationPayment = payment;
        this.applicationPayment.paymentRecords = [];
        this.paymentRecord = record;
    }

    applyInstrument(inst: PendingInstrument, amt: number): void {
        this.applicationPayment.amountPaid += amt;
        this.applicationPayment.amountDue -= amt;
        if (amt > 0) {

            this.applicationPayment.paymentStatus = this.applicationPayment.amountDue == 0 ? PaymentStatus.Paid : PaymentStatus.Partial;
            if (this.paymentRecord == null) {
                this.paymentRecord = {
                    id: 0,
                    applicationPaymentId: this.applicationPaymentId,
                    amount: amt,
                    tokenId: inst.tokenId,
                    paymentType: inst.instrumentType,
                    instrumentAmount: inst.instrumentAmount,
                    token: inst.token
                } as Payment;
            } else {
                this.paymentRecord.instrumentAmount = inst.instrumentAmount;
                this.paymentRecord.amount = amt;
                this.paymentRecord.tokenId = inst.tokenId;
                this.paymentRecord.paymentType = inst.instrumentType;
            }
        } else {
            this.applicationPayment.paymentStatus = this.applicationPayment.amountPaid == 0 ? PaymentStatus.Pending : PaymentStatus.Partial;
            if (this.paymentRecord != null) {
                this.paymentRecord.amount += amt;
                if (this.paymentRecord.amount <= 0) {
                    this.paymentRecord = null;
                } else {
                    if (this.paymentRecord.paymentType === PaymentType.PaidCash) {
                        this.paymentRecord.token = writeToken(this.paymentRecord.amount, { id: inst.id, instrumentType: PaymentType.PaidCash, instrumentAmount: this.paymentRecord.amount, appliedAmount: this.paymentRecord.amount, token: "", receivedDate: this.paymentRecord.paymentReceivedDate } as PaymentInstrument)
                    } else {
                        
                        this.paymentRecord.token = writeToken(this.paymentRecord.amount, { id: inst.id, instrumentType: inst.instrumentType, instrumentAmount: inst.instrumentAmount, appliedAmount: this.paymentRecord.amount, token: inst.tokenId, receivedDate: this.paymentRecord.paymentReceivedDate } as PaymentInstrument);
                    }
                }
            }
        }
       
    }

    toApplicationPayment(): ApplicationPayment {
        this.applicationPayment.paymentRecords = [this.paymentRecord];
        return this.applicationPayment;
    }

    toPaymentRecord(): Payment {
        return this.paymentRecord;
    }
}

export interface PendingApplicationPayment extends ApplicationPayment {
    unappliedFunds: number;
}

export interface ProofOfPurchase {
    documentTypeId: ProofOfPurchaseTypes;
    documentId: string,
    documentUrl?: string;
}

export class PaymentSearchCriteria {
    id?: string;
    applicationNumber: string;
    applicationNumbers: string[] = [];
    deleteStatus: DeleteStatus;
    fromDate?: Date | null;
    inStatuses: PaymentStatus[];
    organizationId: string;
    toDate?: Date | null;
    fromSubmissionDate?: Date | null;
    toSubmissionDate?: Date | null;
    inTypes: PaymentType[];
    hasPayments?: boolean;

    constructor(org: string, applicationNumber: string, statuses: PaymentStatus[], types: PaymentType[] = []) {
        this.organizationId = org;
        this.applicationNumber = applicationNumber;
        this.inStatuses = statuses;
        this.deleteStatus = DeleteStatus.ActiveOnly;      
        if (types.length > 0) this.inTypes = types;
    }
}

export enum DeleteStatus {
    ActiveOnly = 0,
    DeletedOnly = 1,
    All = 2
}

export enum ProofOfPurchaseTypes {
    Invoice = "Invoice",
    Receipt = "Receipt"
}

export enum PaymentStatus {
    Pending = "Pending",
    PendingReceipt = "Pending Receipt",
    Paid = "Paid",
    Refunded = "Refunded",
    Cancelled = "Cancelled",
    Partial = "Partial",
    Overdue = "Overdue",
    Overpaid = "Overpaid"
}

export enum PaymentType {
    ACH = "Check",
    Credit = "Credit",
    LocalCheck = "LocalCheck",
    MoneyOrder = "MoneyOrder",
    PaidCash = "PaidCash"
}

export interface PaymentInstrument {
    id: string;
    instrumentType: string;
    instrumentAmount: number;
    appliedAmount: number;
    token: string;
    receivedDate: Date;
}

export class PendingInstrument {
    id: string;
    instrumentType: string;
    instrumentAmount: number;
    appliedAmount: number;
    fundsRemaining: number;
    tokenId: string;
    originalPayments: Payment[] = [];

    get hasError(): boolean {
        return this.appliedAmount > this.instrumentAmount || this.fundsRemaining < 0;
    }

    get instrumentDescription(): string {
        let result: string = this.token;        
        if (this.instrumentAmount != this.fundsRemaining) result = `${result} - $${this.instrumentAmount - this.fundsRemaining} used`;
        return result;
    }

    get token(): string {
        let result: string = "";
        let type: string = "";
        let token: string = "";
        switch (this.instrumentType) {
            case PaymentType.ACH:
                type = "ACH";
                token = `#${this.tokenId}`;
                break;
            case PaymentType.Credit:
                type = "Credit Authorization";
                token = ` #${this.tokenId}`;
                break;
            case PaymentType.PaidCash:
                type = "Cash";
                break;
            case PaymentType.LocalCheck:
                type = "Check";
                token = ` #${this.tokenId}`;
                break;
            case PaymentType.MoneyOrder:
                type = "Money Order";
                token = ` #${this.tokenId}`;
                break;
        }
        result = `${type}${token} in the amount of ${this._pipe.transform(this.instrumentAmount)}`;
        return result;
    }

    private _pipe: CurrencyPipe;

    constructor(payment: Payment, pipe: CurrencyPipe) {
        this.id = payment.id.toString();
        this.originalPayments.push(payment);
        this.instrumentType = payment.paymentType;
        this.instrumentAmount = payment.instrumentAmount;
        this.fundsRemaining = (payment.instrumentAmount == payment.amount) ? payment.instrumentAmount : payment.instrumentAmount - payment.amount;
        this.appliedAmount = 0;
        this.tokenId = payment.tokenId;
        this._pipe = pipe;
    }

    applyFunds(toApply: number): void {
        this.fundsRemaining -= toApply;
        this.appliedAmount += toApply;
    }
}

export function writePendingToken(amount: number, instrument: PendingInstrument): string {
    let token = "";
    if (instrument.instrumentType.indexOf("Cash") !== -1) {
        token = `Cash in the amount of $${amount}`;
    }

    if (instrument.instrumentType.indexOf("Check") !== -1) {
        if (amount !== instrument.instrumentAmount) {
            token = `Check #${instrument.tokenId} in the amount of $${instrument.instrumentAmount} - $${amount} applied`;
        } else {
            token = `Check #${instrument.tokenId} in the amount of $${instrument.instrumentAmount}`;
        }
    }
    if (instrument.instrumentType.indexOf("MoneyOrder") !== -1) {
        if (amount !== instrument.instrumentAmount) {
            token = `Money Order #${instrument.tokenId} in the amount of $${instrument.instrumentAmount} - $${amount} applied`;
        } else {
            token = `Money Order #${instrument.tokenId} in the amount of $${instrument.instrumentAmount}`;
        }
    }
    if (instrument.instrumentType.indexOf("Credit") !== -1) {
        if (amount !== instrument.instrumentAmount) {
            token = `Credit Authorization #${instrument.tokenId} in the amount of $${instrument.instrumentAmount} - $${amount} applied`;
        } else {
            token = `Credit Authorization #${instrument.tokenId} in the amount of $${instrument.instrumentAmount}`;
        }
    };

    return token;
}


export function writeToken(amount: number, instrument: PaymentInstrument): string {
    let token = "";
    if (instrument.instrumentType.indexOf("Cash") !== -1) {
        token = `Cash in the amount of $${amount}`;
    }

    if (instrument.instrumentType.indexOf("Check") !== -1) {
        if (amount !== instrument.instrumentAmount) {
            token = `Check #${instrument.token.replace("#", "")} in the amount of $${instrument.instrumentAmount} - $${amount} applied`;
        } else {
            token = `Check #${instrument.token.replace("#", "")} in the amount of $${instrument.instrumentAmount}`;
        }
    }
    if (instrument.instrumentType.indexOf("MoneyOrder") !== -1) {
        if (amount !== instrument.instrumentAmount) {
            token = `Money Order #${instrument.token.replace("#", "")} in the amount of $${instrument.instrumentAmount} - $${amount} applied`;
        } else {
            token = `Money Order #${instrument.token.replace("#", "")} in the amount of $${instrument.instrumentAmount}`;
        }
    }
    if (instrument.instrumentType.indexOf("Credit") !== -1) {
        if (amount !== instrument.instrumentAmount) {
            token = `Credit Authorization #${instrument.token.replace("#", "")} in the amount of $${instrument.instrumentAmount} - $${amount} applied`;
        } else {
            token = `Credit Authorization #${instrument.token.replace("#", "")} in the amount of $${instrument.instrumentAmount}`;
        }
    };

    return token;
}

export class PaymentTypeDisplay {
    id: string;
    isCash: boolean;  
    instruments: PaymentInstrument[];

    get cashAmount(): number {
        if (this.instruments == null) return 0;
        let cashInstruments = this.instruments.filter(i => i.instrumentType == PaymentType.PaidCash);
        let sum = 0;
        cashInstruments.forEach(c => sum += c.instrumentAmount);
        return sum;
    }

    constructor(id: string, isCash: boolean) {
        this.id = id;
        this.instruments = [];
        this.isCash = isCash;
    }
}

export class PaymentTokenDisplay {
    instrumentType: string;
    instrumentToken: string;
    instrumentAmount: number;
    appliedAmount: number;

    constructor(token: string) {
        this.parseToken(token);
    }

    public parseToken(token: string): void {
        const tokenParts = token.split(" ");
        if (tokenParts[0].indexOf("Cash") !== -1) {
            this.instrumentType = "Cash";
        }
        if (tokenParts[0].indexOf("Check") !== -1) {
            this.instrumentType = "Check";
        };
        if (tokenParts[0].indexOf("Money") !== -1 && tokenParts[1].indexOf("Order") !== -1) {
            this.instrumentType = "Money Order";
        }
        if (tokenParts[0].indexOf("Credit") !== -1) {
            this.instrumentType = "Credit";
        }
        switch (this.instrumentType) {
            case "Cash":
                this.instrumentAmount = Number(tokenParts[5].replace("$", "").replace(",", ""));
                break;
            case "Check":
                this.instrumentAmount = Number(tokenParts[6].replace("$", "").replace(",", ""));
                this.instrumentToken = tokenParts[1];
                this.appliedAmount = (tokenParts.length === 10)
                    ? this.appliedAmount = Number(tokenParts[8].replace("$", "").replace(",", ""))
                    : 0;
                break;
            case "Money Order":
            case "Credit":
                this.instrumentAmount = Number(tokenParts[7].replace("$", "").replace(",", ""));
                this.instrumentToken = tokenParts[2];
                this.appliedAmount = Number(tokenParts.length === 11)
                    ? this.appliedAmount = Number(tokenParts[9].replace("$", "").replace(",", ""))
                    : 0;
                break;

        }
    }
}

export enum AdjustmentStep {
    SelectApplicationAndPayments,
    ReassignPayments,
    ConfirmSelections
}


export interface PaymentRecordAdjustmentDocument {
    id?: string;
    requestId: string;
    blobUri: string;
    fileName: string;
    extension: string;
    type: string;
    uploadedById: string;
    uploadedByName: string;
    onBaseDocumentType: string;
}

export interface PaymentRecordAdjustmentNote {
    id?: string;
    requestId: string;
    createdById: string;
    createdByName: string;
    createdDate: Date;
    text: string;
    replyTo?: string;
}

export interface PaymentRecordAdjustment {
    adjustmentId?: string;
    requestId: string;
    paymentRecordId: number;
    fromApplicationPaymentId: string;
    toApplicationPaymentId: string;
    fromApplicationNumber: string;
    toApplicationNumber: string;
    fromPaymentDescription: string;
    toPaymentDescription: string;
    amountApplied: number;
    instrumentToken: string;
    instrumentAmount: number;
    paymentType: string;
    tokenId?: string;
    paymentDate: Date;
}

export interface PaymentRecordAdjustmentRequest {
    requestId: string;
    requestedById?: string;
    requestedByName?: string;
    approvedById?: string;
    approvedByName?: string;
    requestedDate?: Date;
    approvedDate?: Date;
    paymentRecordAdjustmentDocuments: PaymentRecordAdjustmentDocument[];
    paymentRecordAdjustmentNotes: PaymentRecordAdjustmentNote[];
    paymentRecordAdjustments: PaymentRecordAdjustment[];
    requestStatus: AdjustmentRequestStatus;
    lastUpdatedDate?: Date;
    lastUpdatedByName?: string;
    lastUpdatedById?: string;
    deniedById?: string;
    deniedByName?: string;
    deniedDate?: Date;
}

export enum AdjustmentRequestStatus {
    Pending = "Pending",
    Denied = "Denied",
    Approved = "Approved"
}

export interface PaymentRecordAdjustmentRequestSearchSpec {
    requestId?: string;
    inStatuses?: AdjustmentRequestStatus[];
    dateRequestedStart?: Date;
    dateRequestedEnd?: Date;
    dateApprovedStart?: Date;
    dateApprovedEnd?: Date;
}

export interface PaymentReceivedInformation {
    applicationPaymentId: string;
    paymentReceivedDate: Date;
    paymentReceivedById: string;
    paymentReceivedByName: string;
}